import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { apiLink } from '../Common/common';
import { oreOrderApi } from '../redux/services/oreOrderApi';
import { oreCuttingApi } from '../redux/services/oreCuttingApi';
import { oreStitchingApi } from '../redux/services/oreStitchingApi';
import { orePackingApi } from '../redux/services/orePackingApi';
import { oreBillApi } from '../redux/services/oreBillApi';
import { steissueApi } from '../redux/services/steissueApi';
import { stepurchaseApi } from '../redux/services/stepurchaseApi';
import { emeattendanceApi } from '../redux/services/emeattendanceApi';
import { dashboardApi } from '../redux/services/dashboardApi';

interface SSEContextType {
  isConnected: boolean;
}

const SSEContext = createContext<SSEContextType>({ isConnected: false });

export const SSEProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const gdata = useSelector((state: RootState) => state.gdata);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!gdata.company) {
      setIsConnected(false);
      return;
    }

    const sseUrl = `${apiLink}events/subscribe/${gdata.company}`;
    let eventSource: EventSource | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const connect = () => {
      try {
        eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          setIsConnected(true);
        };

        eventSource.addEventListener('connected', () => {
          setIsConnected(true);
        });

        // Real-time Order events
        eventSource.addEventListener('ORDER_CHANGED', () => {
          dispatch(oreOrderApi.util.invalidateTags(['Order']));
          dispatch(dashboardApi.util.invalidateTags(['Dashboard']));
        });

        // Real-time Cutting events
        eventSource.addEventListener('CUTTING_CHANGED', () => {
          dispatch(oreCuttingApi.util.invalidateTags(['Cutting']));
          dispatch(oreOrderApi.util.invalidateTags(['Order']));
        });

        // Real-time Stitching events
        eventSource.addEventListener('STITCHING_CHANGED', () => {
          dispatch(oreStitchingApi.util.invalidateTags(['Stitching']));
          dispatch(oreOrderApi.util.invalidateTags(['Order']));
        });

        // Real-time Packing events
        eventSource.addEventListener('PACKING_CHANGED', () => {
          dispatch(orePackingApi.util.invalidateTags(['Packing']));
          dispatch(oreOrderApi.util.invalidateTags(['Order']));
        });

        // Real-time Bill / Sales events
        eventSource.addEventListener('BILL_CHANGED', () => {
          dispatch(oreBillApi.util.invalidateTags(['Bill']));
          dispatch(dashboardApi.util.invalidateTags(['Dashboard']));
        });

        // Real-time Stock Issue events
        eventSource.addEventListener('ISSUE_CHANGED', () => {
          dispatch(steissueApi.util.invalidateTags(['STEIssue']));
          dispatch(oreOrderApi.util.invalidateTags(['Order']));
        });

        // Real-time Stock Purchase events
        eventSource.addEventListener('PURCHASE_CHANGED', () => {
          dispatch(stepurchaseApi.util.invalidateTags(['STEPurchase']));
          dispatch(dashboardApi.util.invalidateTags(['Dashboard']));
        });

        // Real-time Attendance events
        eventSource.addEventListener('ATTENDANCE_CHANGED', () => {
          dispatch(emeattendanceApi.util.invalidateTags(['Attendance']));
        });

        // Real-time Dashboard events
        eventSource.addEventListener('DASHBOARD_CHANGED', () => {
          dispatch(dashboardApi.util.invalidateTags(['Dashboard']));
        });

        eventSource.onerror = () => {
          setIsConnected(false);
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }
          // Reconnect after 5 seconds on connection error
          reconnectTimeout = setTimeout(() => {
            connect();
          }, 5000);
        };
      } catch (err) {
        console.error('Failed to establish SSE connection:', err);
        setIsConnected(false);
      }
    };

    connect();

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      setIsConnected(false);
    };
  }, [gdata.company, dispatch]);

  return (
    <SSEContext.Provider value={{ isConnected }}>
      {children}
    </SSEContext.Provider>
  );
};

export const useSSE = () => useContext(SSEContext);
export default SSEProvider;
